import { unsubscribeComponent } from 'Src/ng2/shared/helpers/unsubscribe-decorator/unsubscribe-decorators.helper';
import {
  TSortDirections,
  SortAndFilterService,
  Direction,
} from './../../../shared/services/list-services/sort-and-filter.service';
import {
  Component,
  ComponentFactoryResolver,
  ElementRef,
  Inject,
  Input,
  OnInit,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { IStateParamsService, IStateService } from 'angular-ui-router';
import { compact as _compact, reduce as _reduce } from 'lodash';
import { BehaviorSubject, Observable, Unsubscribable, Observer, iif } from 'rxjs';
import { of } from 'rxjs/observable/of';
import { filter, switchMap, take, tap } from 'rxjs/operators';
import { SpinnerModal } from 'Src/app/shared/modals/status-spinner/status-spinner.service';
import { FixedToInfiniteViewComponent } from 'Src/ng2/shared/components/list/fixed-to-infinite-view-container/fixed-to-infinite-view.component';
import { IListConfig, IRowData } from 'Src/ng2/shared/models/list-models';
import { ObjectCache } from 'Src/ng2/shared/services/object-cache/object-cache.service';
import { IGroupData } from './../../../shared/models/list-models';
import {
  BackgroundJobNotificationService,
  TValidBackgroundJob,
} from './../../../shared/services/background-job-notification-service/background-job-notification-service';
import { ListSummaryGroupings } from './../../../shared/services/list-summary-groupings/list-summary-groupings.service';
import { IListData } from './../../../shell/content-tools/content-tools.component';
import { ToggleBatchActions, UpdateSelectedStudentIds } from './../../../store/actions/batch-actions-actions';
import { IFocus } from './../academic-list-v2/academic-list-data/academic-data.service';
import { BatchActionsService } from './../services/batch-actions/batch-actions.service';
import { ListHeaderService } from './../services/list-headers/list-headers.service';
import { ListNavigationService } from './../services/list-navigation/list-navigation.service';
import { MadlibService } from './../services/madlib/madlib.service';
import { StudentsListDataService } from './students-list-data/students-list-data.service';

/* istanbul ignore next -- Add to address istanbul bug where constuctor is registering as a branch: https://github.com/istanbuljs/istanbuljs/issues/70 */
@Component({
  selector: 'students-list-v2',
  template: require('./students-list-v2.component.html'),
  styles: [require('./students-list-v2.component.scss')],
})
@unsubscribeComponent
export class StudentsListContainer extends FixedToInfiniteViewComponent implements OnInit {
  public isRefactoredList: boolean = true;
  public fociData$: Observable<any>;
  public madlibModel: FormGroup;
  public dataSource$: Observable<any>;
  public foci: IFocus[];
  public fociByCategory: Array<{ categoryLabel: string; foci: IFocus[] }>; // TODO: Don't currently need.
  public activeList: 'INFINITE' | 'FIXED';
  public batchActionsHash;
  public noDataMessage: any;

  // Background jobs that this view needs to refetch on
  public backgroundJobDependencies: TValidBackgroundJob[] = [
    'BulkStudentUpdate',
    'BulkStudentSupportCreate',
    'BulkStudentSupportUpdate',
  ];

  // MetaData
  public listData: IListData;

  public pageHeaderMeta = {
    title: 'Students',
    subTitle: null,
    icon: {
      tooltip: null,
    },
  };

  public filterFormControl: FormControl;
  public sortKey$: BehaviorSubject<string>;
  public sortDirection$: BehaviorSubject<TSortDirections>;
  public updateSort: Function;

  // SuperProps
  public onFixed: Function;
  public onInfinite: Function;
  public onBatchAction: Function;
  public onRowClick: Function;

  public uiRowData;
  public visualGroupingData: IGroupData;
  public onUiRowDataUpdate: Function;

  public columns;
  public dynamicColumns = new BehaviorSubject(null);
  public dynamicColumnIndexMap = new BehaviorSubject(null);
  public columnIndexMap: { [columnKey: string]: number };
  public groupingData$ = new BehaviorSubject(null);
  public listConfig: IListConfig = {
    listType: 'STUDENT',
    noDataMessage: 'No students to display',
    sortableColumns: true,
  };

  // SubSinks
  public notificationServiceSub: Unsubscribable;
  public batchActionsModeSubscription: Unsubscribable;
  public filterSub: Unsubscribable;

  // Bindings
  @Input() schoolId: string;
  @Input() batchActionsMode$: Observable<any>;

  // Refs
  @ViewChild('entry', { read: ViewContainerRef, static: true }) entry: ViewContainerRef;
  @ViewChild('listContent', { static: true }) listContent: ElementRef;

  // DI
  constructor(
    public resolver: ComponentFactoryResolver,
    public listSummaryGroupings: ListSummaryGroupings,
    public madlibService: MadlibService,
    public objectCache: ObjectCache,
    public notificationService: BackgroundJobNotificationService,
    public studentsDataService: StudentsListDataService,
    private store: Store<any>,
    private batchActionsService: BatchActionsService,
    private navigationService: ListNavigationService,
    private listHeaderService: ListHeaderService,
    @Inject('SpinnerModal') public spinnerService: SpinnerModal,
    @Inject('$state') public $state: IStateService,
    @Inject('$stateParams') private $stateParams: IStateParamsService,
  ) {
    // Extends the fixed-to-infinite functionality
    super(resolver);
  }

  ngOnInit(): void {
    // Clear the cache on initialization. User *may* have changed schools.
    this.studentsDataService.clearFociCache();

    // Set initial state based on url params
    this.filterFormControl = new FormControl(this.$stateParams.search || null);
    this.sortKey$ = new BehaviorSubject(this.$stateParams.sortKey || 'STUDENT_NAME');
    this.sortDirection$ = new BehaviorSubject<TSortDirections>(this.$stateParams.sortDirection || Direction.asc);
    // Super Props
    this.updateSort = (sortKey: string) => {
      SortAndFilterService.updateSortCol(sortKey, this.sortKey$, this.sortDirection$);
      this.navigationService.updateUrl(this.sortKey$, this.sortDirection$, this.filterFormControl, this.madlibModel);
    };

    // Overwrite super methods
    this.onBatchAction = this.formatAndFireBatchActionEvt;
    this.onRowClick = this.navigationService.goToStudentProfile;
    // Called by super; when in each of the list modes
    this.onFixed = this.updateCssForListType('FIXED');
    this.onInfinite = this.updateCssForListType('INFINITE');

    this.onUiRowDataUpdate = ($event: IGroupData) => {
      const groupings = Object.values($event);
      this.uiRowData = groupings;
      this.updateContentToolsData(groupings);
    };

    // Update URL on filter change
    this.filterSub = this.filterFormControl.valueChanges
      .pipe(
        tap(() =>
          this.navigationService.updateUrl(
            this.sortKey$,
            this.sortDirection$,
            this.filterFormControl,
            this.madlibModel,
          ),
        ),
      )
      .subscribe();
    // Listen for completed background jobs and refetch data as needed
    this.notificationServiceSub = this.notificationService
      .getMessage()
      .pipe(
        filter(({ backgroundJob }) => this.backgroundJobDependencies.includes(backgroundJob)),
        tap(() => this.initializeStudentsList(this.schoolId, this.$stateParams, false)),
      )
      .subscribe();
    // Initialize batchAction data structure with helper methods. User to keep track of selected students.
    this.batchActionsHash = this.batchActionsService.initializeStudentBatchActionsHash();
    this.batchActionsModeSubscription = this.batchActionsMode$
      .pipe(tap((mode: boolean) => (!mode ? this.resetBatchActions() : null)))
      .subscribe();

    // kick off data pipeline
    this.initializeStudentsList(this.schoolId, this.$stateParams, true);
  }

  // getFocusData---createMadlibModel---getGroupingData---createListContainer--|
  initializeStudentsList(schoolId: string, $stateParams: IStateParamsService, loadContainer: boolean): void {
    this.getFociData$(schoolId)
      .pipe(
        // Format and set the partials to fit the nv-dropdown
        tap(fociPartials => (this.fociByCategory = this.madlibService.getFormattedFoci(fociPartials))),
        // Once we know the default focus, fetch complete data to populate corresponding filter and grouping options
        switchMap(fociPartials =>
          this.getFociData$(schoolId, this.madlibService.getDefaultFocus(fociPartials, $stateParams)),
        ),
        switchMap(([completeFocusData]) =>
          of(this.madlibService.setMadlibModel(this.madlibService.formatFocus(completeFocusData), $stateParams)),
        ),
        // Set madlib model with focus containing all data
        tap(madlibModel => (this.madlibModel = madlibModel)),
        switchMap(madlibModel => this.getGrouping$(madlibModel)),
        tap(() =>
          this.navigationService.updateUrl(
            this.sortKey$,
            this.sortDirection$,
            this.filterFormControl,
            this.madlibModel,
          ),
        ),
        // Set side-bar summary/csv-export etc.
        tap(groupingData => {
          this.updateGroupingDependentProps(groupingData);
          if (loadContainer) this.createListContainer();
        }),
      )
      .subscribe({ complete: () => this.spinnerService.deactivateSpinner() });
  }

  private getGrouping$(madlibModel) {
    return this.studentsDataService.getStudentsListGroupingData$(
      this.madlibService.getGroupingDataPayload(this.schoolId, madlibModel.value),
    );
  }

  onMadlibChange(evt: { dimension: string; data: any }, schoolId: string, madlibModel: FormGroup): void {
    this.spinnerService.activateSpinner('Loading...');
    iif(
      () => evt.dimension === 'focus',
      this.madlibService.getModelForFocusChange(schoolId, madlibModel, evt, this.getFociData$.bind(this)),
      this.madlibService.getModelForNonFocusChange(madlibModel, evt),
    )
      .pipe(
        tap(() =>
          this.navigationService.updateUrl(
            this.sortKey$,
            this.sortDirection$,
            this.filterFormControl,
            this.madlibModel,
          ),
        ),
        switchMap(madlibModel => this.getGrouping$(madlibModel)),
        tap(groupingData => this.updateGroupingDependentProps(groupingData)),
      )
      .subscribe({ complete: () => this.spinnerService.deactivateSpinner() });
  }

  updateCssForListType(listMode: 'INFINITE' | 'FIXED'): Function {
    return (instance?: ElementRef): void => {
      this.activeList = listMode;
      const addClassForListMode = listMode === 'INFINITE' ? 'infinite' : 'fixed';
      this.listContent.nativeElement.classList.add(addClassForListMode);
      const removeClassForListMode = listMode === 'INFINITE' ? 'fixed' : 'infinite';
      this.listContent.nativeElement.classList.remove(removeClassForListMode);
      this.pageHeaderMeta =
        listMode === 'INFINITE'
          ? { ...this.pageHeaderMeta, ...{ icon: this.listHeaderService.getInfiniteHeaderIcon(instance) } }
          : { ...this.pageHeaderMeta, ...{ icon: this.listHeaderService.getFixedHeaderIcon() } };
    };
  }

  getFociData$(schoolId: string, focusKey?: string): Observable<any> {
    return this.studentsDataService.getStudentsListFocusData$(schoolId, focusKey).pipe(
      switchMap(({ data: { StudentsFocus } }) => of(StudentsFocus)),
      take(1),
    );
  }

  formatAndFireBatchActionEvt($event: { updateAll: boolean; data: IRowData[]; level: 'SECTION' | 'ROW' }): void {
    const batchData = this.batchActionsService.formatBatchActionEvt($event);
    this.batchActionsHash.updateStudentIds(batchData);
    const studentIds = this.batchActionsHash.getStudentIds();
    this.store.dispatch(new UpdateSelectedStudentIds(studentIds));
  }

  resetBatchActions(): void {
    this.batchActionsHash.reset();
    this.store.dispatch(new UpdateSelectedStudentIds([]));
    this.store.dispatch(new ToggleBatchActions(false));
  }

  updateContentToolsData(groupData: IGroupData[], visibleGroupings?: string[]): void {
    const groupings = visibleGroupings ? groupData.filter(group => visibleGroupings.includes(group.key)) : groupData;
    this.listData = this.listSummaryGroupings.getExportListData({
      groupings,
      columns: this.madlibService.getColumnDataBasedOnMadlibModel(this.madlibModel).columns,
      madlibModel: this.madlibModel,
      listType: 'Students',
    });
    this.listSummaryGroupings.setGroupingsV2(this.madlibModel.value, groupings);
  }

  updateGroupingDependentProps({ data: { StudentGroupings } }): void {
    const { columns, columnIndexMap } = this.madlibService.getColumnDataBasedOnMadlibModel(this.madlibModel);
    this.dynamicColumns.next(columns);
    this.dynamicColumnIndexMap.next(columnIndexMap);
    this.groupingData$.next(StudentGroupings);
    this.updateContentToolsData(StudentGroupings);
  }
}
